import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { SectionHeader } from "@/components/common/Widgets";
import { fmtDate } from "@/lib/format";
import { CheckCircle2, XCircle, ShieldAlert, Inbox, History } from "lucide-react";

export default function MakerCheckerPage() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("PENDING");
  const [remarks, setRemarks] = useState({});

  const load = async () => {
    if (status === "ACTIONED_BY_ME") {
      const r = await api.get("/maker-checker/actioned-by-me");
      setItems(r.data);
    } else {
      const r = await api.get("/maker-checker/pending", { params: { status } });
      setItems(r.data);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const decide = async (id, decision) => {
    const r = remarks[id] || "";
    try {
      await api.post("/maker-checker/decide", { pending_id: id, decision, remarks: r });
      toast.success(`${decision}d`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4">
      <SectionHeader title="Maker-Checker Queue" subtitle="Two-eyes principle for critical actions." action={(
        <div className="flex gap-2 flex-wrap">
          {["PENDING", "APPROVED", "REJECTED"].map(s => (
            <Button key={s} variant={status === s ? "default" : "outline"} size="sm" onClick={() => setStatus(s)} data-testid={`mc-filter-${s}`}>{s}</Button>
          ))}
          <Button
            variant={status === "ACTIONED_BY_ME" ? "default" : "outline"}
            size="sm"
            onClick={() => setStatus("ACTIONED_BY_ME")}
            data-testid="mc-filter-ACTIONED_BY_ME"
          >
            <History className="h-3.5 w-3.5 mr-1" />Actioned by me
          </Button>
        </div>
      )} />
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground"><tr><th className="text-left px-3 py-2.5">When</th><th className="text-left px-3 py-2.5">Action</th><th className="text-left px-3 py-2.5">Maker</th>{status === "ACTIONED_BY_ME" && <th className="text-left px-3 py-2.5">Decided</th>}<th className="text-left px-3 py-2.5">Application</th><th className="text-left px-3 py-2.5">Status</th>{status === "PENDING" && <th></th>}</tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={status === "PENDING" ? 6 : status === "ACTIONED_BY_ME" ? 6 : 5} className="text-center py-12 text-muted-foreground"><Inbox className="h-7 w-7 mx-auto mb-2" />No items.</td></tr>}
              {items.map(p => (
                <tr key={p.id} className="border-t border-border align-top">
                  <td className="px-3 py-2 text-xs text-muted-foreground mono">{fmtDate(p.requested_at)}</td>
                  <td className="px-3 py-2"><div className="font-medium">{p.action_description}</div><div className="text-[11px] text-muted-foreground mono">{p.action_type}</div>{p.remarks && <div className="text-xs mt-1 text-muted-foreground">"{p.remarks}"</div>}</td>
                  <td className="px-3 py-2 text-xs">{p.maker_name} <span className="text-muted-foreground">({p.maker_role})</span></td>
                  {status === "ACTIONED_BY_ME" && (
                    <td className="px-3 py-2 text-xs text-muted-foreground mono">{p.decided_at ? fmtDate(p.decided_at) : "—"}{p.decision_remarks && <div className="text-xs mt-1 not-italic text-muted-foreground">"{p.decision_remarks}"</div>}</td>
                  )}
                  <td className="px-3 py-2 text-xs"><Link to={`/applications/${p.application_id}`} className="underline text-accent">{p.application_id?.slice(0, 12)}…</Link></td>
                  <td className="px-3 py-2">{p.status === "PENDING" ? <Badge variant="outline" className="text-warning border-warning/40">PENDING</Badge> : p.status === "APPROVED" ? <Badge variant="outline" className="text-success border-success/40">APPROVED</Badge> : <Badge variant="outline" className="text-destructive border-destructive/40">REJECTED</Badge>}</td>
                  {status === "PENDING" && (
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-2 min-w-[280px]">
                        <Textarea placeholder="Remarks (optional)" rows={2} value={remarks[p.id] || ""} onChange={(e) => setRemarks(r => ({ ...r, [p.id]: e.target.value }))} className="text-xs" />
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" variant="outline" onClick={() => decide(p.id, "REJECT")} data-testid={`mc-reject-${p.id}`}><XCircle className="h-3.5 w-3.5 mr-1 text-destructive" />Reject</Button>
                          <Button size="sm" onClick={() => decide(p.id, "APPROVE")} data-testid={`mc-approve-${p.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Approve</Button>
                        </div>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <div className="text-xs text-muted-foreground flex items-center gap-1.5"><ShieldAlert className="h-3.5 w-3.5" />Maker and Checker must be different users. Items you have already submitted or actioned are hidden from your PENDING queue (Segregation of Duties). Use <b>Actioned by me</b> to review your historical decisions.</div>
    </div>
  );
}
